import { useCallback, useEffect, useMemo, useState } from 'react'
import { FileText, Download, Loader2, Info, AlertTriangle, CheckCircle2, ShieldCheck } from 'lucide-react'
import { apiGet, postJson } from '../../lib/api.js'
import { apiErrorText } from '../../lib/apiError.js'
import { fmtThaiDate } from '../../lib/datetime.js'

const FIELD = 'w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-[13.5px] text-slate-800 outline-none focus:border-brand-400 focus:ring-4 focus:ring-brand-500/10'

/**
 * พื้นที่ของฉัน › ขอเอกสาร — the employee issues their own หนังสือรับรองเงินเดือน
 * or หนังสือรับรองการทำงาน and downloads the PDF, with no HR step at all.
 *
 * 🔑 THERE IS NO REQUEST AND NO QUEUE HERE, ON PURPOSE. Everything the certificate
 * asserts — name, position, start date, salary — is already authoritative in the
 * CRM, so an approval step would be HR retyping its own data while someone waits a
 * day for a letter the bank wanted today. HR audits the register instead
 * (owner's decision, 2026-08-26). The doctor portal has run this same model for
 * dentists since before this page existed.
 *
 * The card above the form is a PREVIEW, and its figure comes from GET
 * /api/me/document — the same certSalary() the PDF itself uses. It is deliberately
 * not recomputed here from /api/me/salary: two implementations of "what does this
 * person earn" would eventually disagree, and the one on the letter is the one a
 * bank acts on.
 *
 * ⚠️ Nothing this page sends can change what the document says. The body is four
 * fields — doc, lang, purpose, purpose_note — and every asserted fact is read
 * server-side from the session's own employee_id. See functions/api/me/document.js.
 */

const DOC_TYPES = [
  { id: 'salary_cert', label: 'หนังสือรับรองเงินเดือน', hint: 'ระบุตำแหน่ง วันเริ่มงาน และเงินเดือน', needsSalary: true },
  { id: 'employment_letter', label: 'หนังสือรับรองการทำงาน', hint: 'ระบุตำแหน่งและวันเริ่มงาน ไม่ระบุเงินเดือน', needsSalary: false },
]

const PURPOSES = [
  { id: 'general', label: 'ใช้ทั่วไป' },
  { id: 'bank', label: 'ยื่นขอสินเชื่อ / บัตรเครดิต' },
  { id: 'rental', label: 'เช่าที่พักอาศัย' },
  { id: 'visa', label: 'ยื่นขอวีซ่า' },
  { id: 'other', label: 'อื่นๆ (ระบุ)' },
]

const LANGS = [
  { id: 'th', label: 'ไทย' },
  { id: 'en', label: 'English' },
]

const DOC_LABEL = Object.fromEntries(DOC_TYPES.map((d) => [d.id, d.label]))
const PURPOSE_LABEL = Object.fromEntries(PURPOSES.map((p) => [p.id, p.label]))

const MONTHS_TH = ['มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน',
                   'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม']

const fmtMoney = (n) => (Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

// "1 มีนาคม 2562" — full month, BE year. Long form, because that is how it prints
// on the letter and the preview must not disagree with it.
function thaiLongDate(v) {
  if (!v) return '—'
  const d = new Date(String(v).length <= 10 ? `${v}T00:00:00` : v)
  if (Number.isNaN(d.getTime())) return '—'
  return `${d.getDate()} ${MONTHS_TH[d.getMonth()]} ${d.getFullYear() + 543}`
}


export default function MeDocument() {
  const [ctx, setCtx] = useState(null)
  const [history, setHistory] = useState([])
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState('')

  const [doc, setDoc] = useState('salary_cert')
  const [lang, setLang] = useState('th')
  const [purpose, setPurpose] = useState('general')
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [formErr, setFormErr] = useState('')
  const [done, setDone] = useState(null)
  // The issue succeeded but the register could not be re-read — see the catch in submit().
  const [historyStale, setHistoryStale] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setErr('')
    try {
      const [c, h] = await Promise.all([
        apiGet('/api/me/document'),
        apiGet('/api/me/documents').catch(() => null),
      ])
      setCtx(c)
      setHistory(Array.isArray(h?.items) ? h.items : [])
    } catch (e) {
      setErr(apiErrorText(e, 'โหลดข้อมูลไม่สำเร็จ'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  // A blob: URL for the returned PDF, so the download never goes through a public
  // Drive link — this document carries an id-card number and a salary. Revoked when
  // another document is generated or the page unmounts; an un-revoked object URL
  // keeps the whole PDF in memory. Same pattern as ContractDialog.jsx.
  const blobUrl = useMemo(() => {
    if (!done?.pdfBase64) return ''
    try {
      const bin = atob(done.pdfBase64)
      const bytes = new Uint8Array(bin.length)
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i)
      return URL.createObjectURL(new Blob([bytes], { type: done.mimeType || 'application/pdf' }))
    } catch { return '' }
  }, [done])
  useEffect(() => () => { if (blobUrl) URL.revokeObjectURL(blobUrl) }, [blobUrl])

  // The download starts by itself — the employee pressed a button that says
  // "ออกเอกสาร", so a second click to actually get the file is a step with no
  // decision in it. The link below stays for a re-download.
  useEffect(() => {
    if (!blobUrl || !done) return
    const a = document.createElement('a')
    a.href = blobUrl
    a.download = done.fileName || 'certificate.pdf'
    document.body.appendChild(a)
    a.click()
    a.remove()
  }, [blobUrl, done])

  const selected = DOC_TYPES.find((d) => d.id === doc)
  const salary = ctx?.salary || null
  // The salary certificate is only issuable once there is a payroll history to
  // state. The employment letter never depends on one.
  const blockedNoSalary = !!selected?.needsSalary && ctx?.eligible && !ctx?.has_salary

  async function submit(e) {
    e.preventDefault()
    setFormErr('')
    if (purpose === 'other' && !note.trim()) { setFormErr('กรุณาระบุวัตถุประสงค์'); return }
    setBusy(true)
    setDone(null)
    setHistoryStale(false)
    try {
      const res = await postJson('/api/me/document', {
        doc, lang, purpose, purpose_note: purpose === 'other' ? note.trim() : '',
      })
      setDone(res)
      // The new row belongs in the register below without a full reload.
      // 🚨 NOT a bare `catch {}`. The certificate WAS issued and its serial IS burned
      // by the time this runs — so if the refetch fails, the register silently does
      // not grow and the employee is left thinking nothing happened, when in fact a
      // numbered document exists. That is the same failure ebc77b7 spent two days on
      // with the missed-call badge: a broken read and an empty result looked
      // identical. Say so in the console, and tell the user the document is real.
      apiGet('/api/me/documents')
        .then((h) => setHistory(Array.isArray(h?.items) ? h.items : []))
        .catch((e3) => {
          console.warn('[me/document] register refetch failed:', e3?.message || e3)
          setHistoryStale(true)
        })
    } catch (e2) {
      setFormErr(apiErrorText(e2, 'ออกเอกสารไม่สำเร็จ'))
    } finally {
      setBusy(false)
    }
  }

  if (loading) {
    return (
      <div className="max-w-[820px] flex items-center gap-2 text-[13px] text-slate-500 py-10">
        <Loader2 size={15} className="animate-spin" /> กำลังโหลด…
      </div>
    )
  }

  if (err) {
    return (
      <div className="max-w-[820px]">
        <div className="flex items-start gap-2 text-[13px] text-rose-700 bg-rose-50 border border-rose-200 rounded-xl px-3 py-2.5">
          <AlertTriangle size={15} className="mt-0.5 flex-none" /><span>{err}</span>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-[820px] space-y-5">
      {/* Not eligible: guidance, not a failure. Everyone who lands here is a
          legitimate employee who has to ask ฝ่ายบุคคล instead. */}
      {ctx && !ctx.eligible ? (
        <section className="rounded-2xl border border-amber-200 bg-amber-50/60 px-4 sm:px-5 py-4">
          <div className="flex items-start gap-2.5">
            <Info size={16} className="text-amber-600 mt-0.5 flex-none" strokeWidth={1.9} />
            <div className="text-[13px] text-amber-900 leading-relaxed">
              <div className="font-semibold mb-0.5">ยังออกเอกสารเองไม่ได้</div>
              <div>{ctx.detail}</div>
            </div>
          </div>
        </section>
      ) : (
        <>
          {/* What the document will say. Shown BEFORE issuing so a stale position or
              a wrong start date is caught here rather than at a bank counter. */}
          <section className="rounded-2xl border border-slate-200/80 bg-white">
            <header className="flex items-center gap-2 px-4 sm:px-5 py-3.5 border-b border-slate-100">
              <ShieldCheck size={15} className="text-brand-600 flex-none" strokeWidth={1.9} />
              <h2 className="text-[14px] font-semibold text-slate-800">ข้อมูลที่จะปรากฏบนเอกสาร</h2>
            </header>
            <dl className="px-4 sm:px-5 py-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-[13px]">
              <Row label="ชื่อ-นามสกุล" value={[ctx?.employee?.prefix, ctx?.employee?.name].filter(Boolean).join('')} />
              <Row label="ตำแหน่ง" value={ctx?.employee?.position || '—'} />
              <Row label="วันเริ่มงาน" value={thaiLongDate(ctx?.employee?.date_start)} />
              <Row label="ออกในนาม" value={ctx?.company?.name || '—'} />
              {salary?.found > 0 && (
                <div className="sm:col-span-2 pt-2 border-t border-slate-100">
                  <dt className="text-slate-500 mb-1">
                    {salary.uniform ? 'อัตราเงินเดือนเดือนละ' : 'เงินเดือนเฉลี่ยเดือนละ'}
                  </dt>
                  <dd className="text-[17px] font-semibold text-slate-800 tabular-nums">
                    {fmtMoney(salary.amount)} <span className="text-[13px] font-normal text-slate-500">บาท</span>
                  </dd>
                  <p className="mt-1.5 text-[12px] text-slate-500 leading-relaxed">
                    คำนวณจากเงินเดือนและค่าตำแหน่ง {salary.found} เดือนล่าสุด
                    {' '}({salary.months.map((m) => `${MONTHS_TH[m.month - 1]} ${m.year + 543}`).join(' · ')}){' '}
                    — ไม่รวมค่าล่วงเวลา เบี้ยขยัน และไม่หักรายการหักใดๆ
                  </p>
                </div>
              )}
            </dl>
            <p className="px-4 sm:px-5 pb-4 text-[12px] text-slate-500 leading-relaxed">
              หากข้อมูลไม่ถูกต้อง กรุณาแจ้งฝ่ายบุคคลก่อนออกเอกสาร
            </p>
          </section>

          <form onSubmit={submit} className="rounded-2xl border border-slate-200/80 bg-white">
            <header className="flex items-center gap-2 px-4 sm:px-5 py-3.5 border-b border-slate-100">
              <FileText size={15} className="text-brand-600 flex-none" strokeWidth={1.9} />
              <h2 className="text-[14px] font-semibold text-slate-800">ออกเอกสาร</h2>
            </header>

            <fieldset disabled={busy} className="px-4 sm:px-5 py-4 space-y-4">
              <div>
                <span className="block text-[12.5px] text-slate-500 mb-1.5">ประเภทเอกสาร</span>
                <div className="grid gap-2 sm:grid-cols-2">
                  {DOC_TYPES.map((d) => (
                    <button
                      key={d.id} type="button" onClick={() => setDoc(d.id)}
                      className={`text-left rounded-xl border px-3 py-2.5 transition-colors ${
                        doc === d.id
                          ? 'border-brand-400 bg-brand-50/70 ring-4 ring-brand-500/10'
                          : 'border-slate-200 hover:bg-slate-50'}`}
                    >
                      <div className="text-[13.5px] font-medium text-slate-800">{d.label}</div>
                      <div className="text-[12px] text-slate-500 mt-0.5">{d.hint}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <span className="block text-[12.5px] text-slate-500 mb-1.5">ภาษา</span>
                  <div className="flex gap-1 p-1 rounded-xl bg-slate-100 border border-slate-200">
                    {LANGS.map((l) => (
                      <button
                        key={l.id} type="button" onClick={() => setLang(l.id)}
                        className={`flex-1 rounded-lg px-2 py-1.5 text-[13px] transition-colors ${
                          lang === l.id ? 'bg-white text-slate-900 font-medium shadow-sm' : 'text-slate-500'}`}
                      >{l.label}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-[12.5px] text-slate-500 mb-1.5" htmlFor="doc-purpose">วัตถุประสงค์</label>
                  <select id="doc-purpose" className={FIELD} value={purpose} onChange={(e) => setPurpose(e.target.value)}>
                    {PURPOSES.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
                  </select>
                </div>
              </div>

              {purpose === 'other' && (
                <div>
                  <label className="block text-[12.5px] text-slate-500 mb-1.5" htmlFor="doc-note">ระบุวัตถุประสงค์</label>
                  <input
                    id="doc-note" className={FIELD} value={note} maxLength={120}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="เช่น ยื่นสถานทูตญี่ปุ่น"
                  />
                </div>
              )}

              {blockedNoSalary && (
                <div className="flex items-start gap-2 text-[13px] text-amber-900 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2.5">
                  <Info size={15} className="mt-0.5 flex-none text-amber-600" />
                  <span>ยังไม่พบประวัติเงินเดือนในระบบ จึงออกหนังสือรับรองเงินเดือนเองไม่ได้ — กรุณาติดต่อฝ่ายบุคคล</span>
                </div>
              )}

              {formErr && (
                <div className="flex items-start gap-2 text-[13px] text-rose-700 bg-rose-50 border border-rose-200 rounded-xl px-3 py-2.5">
                  <AlertTriangle size={15} className="mt-0.5 flex-none" /><span>{formErr}</span>
                </div>
              )}

              {done && (
                <div className="rounded-xl bg-emerald-50 border border-emerald-200 px-3 py-2.5">
                  <div className="flex items-start gap-2 text-[13px] text-emerald-800">
                    <CheckCircle2 size={15} className="mt-0.5 flex-none" />
                    <div>
                      <div className="font-medium">ออกเอกสารเรียบร้อย — เลขที่ {done.serial}</div>
                      <div className="text-[12px] mt-0.5 text-emerald-700">
                        ไฟล์กำลังดาวน์โหลด หากไม่เริ่มอัตโนมัติ
                        {blobUrl
                          ? <a className="underline ml-1" href={blobUrl} download={done.fileName || 'certificate.pdf'}>กดที่นี่</a>
                          : (done.downloadUrl ? <a className="underline ml-1" href={done.downloadUrl} target="_blank" rel="noopener noreferrer">กดที่นี่</a> : null)}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* The document is real and numbered even though the list below did not
                  refresh. Saying nothing would leave the employee believing the click
                  did nothing and pressing it again — burning a second serial for a
                  document they already have. */}
              {historyStale && (
                <div className="flex items-start gap-2 rounded-xl bg-amber-50 border border-amber-200 px-3 py-2.5 text-[13px] text-amber-900">
                  <AlertTriangle size={15} className="mt-0.5 flex-none text-amber-600" />
                  <span>
                    ออกเอกสารสำเร็จแล้ว แต่โหลดรายการด้านล่างไม่สำเร็จ — <strong>ไม่ต้องกดออกเอกสารซ้ำ</strong> รีเฟรชหน้านี้เพื่อดูรายการ
                  </span>
                </div>
              )}

              <div className="flex items-center gap-3 pt-1">
                <button
                  type="submit"
                  disabled={busy || blockedNoSalary}
                  className="inline-flex items-center gap-1.5 rounded-xl bg-brand-600 text-white px-4 py-2 text-[13.5px] font-medium hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {busy ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />}
                  {busy ? 'กำลังสร้างเอกสาร…' : 'ออกเอกสาร'}
                </button>
                <span className="text-[12px] text-slate-500">ได้ไฟล์ PDF ทันที ไม่ต้องรออนุมัติ</span>
              </div>
            </fieldset>
          </form>
        </>
      )}

      {/* The employee's own register. Its job is the serial number: when a bank rings
          the clinic to verify a letter, this is where they read it back from. */}
      {history.length > 0 && (
        <section className="rounded-2xl border border-slate-200/80 bg-white">
          <header className="flex items-center gap-2 px-4 sm:px-5 py-3.5 border-b border-slate-100">
            <h2 className="text-[14px] font-semibold text-slate-800">เอกสารที่เคยออก</h2>
          </header>
          <ul className="divide-y divide-slate-100">
            {history.map((r) => (
              <li key={r.id} className="px-4 sm:px-5 py-3 flex flex-wrap items-baseline gap-x-3 gap-y-1 text-[13px]">
                <span className="font-medium text-slate-800">{DOC_LABEL[r.doc] || r.doc}</span>
                <span className="text-slate-500">{PURPOSE_LABEL[r.purpose] || r.purpose}</span>
                <span className="ml-auto tabular-nums text-slate-500">{fmtThaiDate(r.created_at)}</span>
                <span className="w-full text-[12px] text-slate-500 tabular-nums">
                  เลขที่ {r.serial || '—'}
                  {r.rendered === false && <span className="ml-2 text-rose-600">· สร้างไฟล์ไม่สำเร็จ</span>}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  )
}

function Row({ label, value }) {
  return (
    <div>
      <dt className="text-slate-500">{label}</dt>
      <dd className="text-slate-800 font-medium mt-0.5">{value || '—'}</dd>
    </div>
  )
}
